/**
 * `VehicleDetailsPage` captures and/or loads vehicle information for the dealer
 * journey.
 *
 * Responsibilities:
 * - Accepts navigation state for an existing quote, a manually entered vehicle,
 *   or an id value (VRN/VIN).
 * - Loads vehicle details via `VehicleApi` for VRN/VIN lookups (with different
 *   behaviour when not on GB sites).
 * - Updates journey state with vehicle details and quote-derived products/
 *   documents/personal details when a quote is supplied.
 * - Coordinates validation/update cycles with `VehiclePage` and navigates to
 *   `Products` on success.
 */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import { VehicleType, VehicleState, QuotesData } from '../common/CommonTypes';
import { VehicleApi } from '../../api/VehicleApi';
import {
  PageAbsolutePath,
  VehicleIdType,
  ProductTypes,
  PageRelativePath
} from '../../consts/Common';
import VehiclePage from '../common/VehiclePage/VehiclePage';
import PageHeader from '../common/PageHeader';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import { ButtonContinue } from '../common/Button/ButtonContinue';
import { ButtonBack } from '../common/Button/ButtonBack';
import { useJourney } from './Layout';
import LoadingContainer from '../common/LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { setCountryCode } from '../common/HelperMethods';
import { CountryIsoCodes } from '../common/Constants';
import { blankVehicle } from '../testData/journeyHelper';
import { useAppSelector } from '../../app-state/Hooks';

export type LocationQuote = {
  quote: QuotesData;
};

export type LocationVehicle = {
  vehicle: VehicleType;
};

export type LocationVehicleId = {
  idValue: string;
  vehicleIdType: VehicleIdType;
};

type LocationState = LocationQuote | LocationVehicle | LocationVehicleId;

const initialVehicleDetails: VehicleType = blankVehicle;

const VehicleDetailsPage = (): JSX.Element => {
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [updateVehicleDetails, setUpdateVehicleDetails] = useState<
    boolean | null
  >(null);
  const [validateVehicleDetails, setValidateVehicleDetails] = useState<
    boolean | null
  >(null);
  const [goForward, setGoForward] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [identificationNumber, setIdentificationNumber] = useState('');

  const {
    journey,
    onUpdateVehicleDetails,
    onUpdateProducts,
    onUpdateProductDocuments,
    onPageChange,
    onUpdateFoundExistingQuotes,
    onUpdateManufacturingCoverageMonths,
    setQuoteId,
    setHasQuoteExpired,
    onUpdateQuotedProducts,
    onUpdateQuotedByDealerReference,
    setPersonalDetails
  } = useJourney();

  const { vehicle } = journey;
  const { vehicleDetails } = vehicle;
  const navigate = useNavigate();
  const { state } = useLocation();
  const appConfig = useAppSelector((s) => s.appConfig);
  const userToken = useAppSelector((s) => s.user.token);

  const cms = useAppSelector((s) => s.content.vehiclePage);
  const productTypeCustomisation = useAppSelector(
    (s) => s.content.productPage.productTypeCustomisation
  );
  const landingPageContentSettings = useAppSelector(
    (s) => s.content.landingPage
  );
  const { t } = useTranslation();
  const {
    hidePurchasePrice,
    hideDeliveryDate,
    hideDealerReference,
    externalReferenceLabelText,
    hideVinInput
  } = cms;
  const showVehiclePurchasePrice = !hidePurchasePrice;
  const showVehicleDeliveryDate = !hideDeliveryDate;
  const showExternalReference = !hideDealerReference;
  const searchCarOnPageLoad = appConfig.countryCode === CountryIsoCodes.GB;

  const showGapDateField = productTypeCustomisation.some(
    (p) => p.productType === ProductTypes.Gap
  );

  const determineIfQuotePassed = (
    toBeDetermined: LocationState
  ): toBeDetermined is LocationQuote => {
    if ((toBeDetermined as LocationQuote).quote) {
      return true;
    }
    return false;
  };

  const determineIfVehiclePassed = (
    toBeDetermined: LocationState
  ): toBeDetermined is LocationVehicle => {
    if ((toBeDetermined as LocationVehicle).vehicle) {
      return true;
    }
    return false;
  };

  const determineIfVehicleIdPassed = (
    toBeDetermined: LocationState
  ): toBeDetermined is LocationVehicleId => {
    if ((toBeDetermined as LocationVehicleId).idValue) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!state) {
      return;
    }

    const hasQuote = determineIfQuotePassed(state as LocationState);

    if (hasQuote) {
      const quote = (state as LocationQuote).quote;

      updateJourneyStateFromQuoteData(quote);

      return;
    }

    const hasVehicle = determineIfVehiclePassed(state as LocationState);

    if (hasVehicle) {
      const locationVehicleDetails = state.vehicle;

      onUpdateVehicleDetails({
        ...vehicle,
        vehicleDetails: locationVehicleDetails
      });

      return;
    }

    const hasVehicleId = determineIfVehicleIdPassed(state as LocationState);

    if (hasVehicleId) {
      const vehicleIdState = state as LocationVehicleId;

      switch (vehicleIdState.vehicleIdType) {
        case VehicleIdType.Vrn:
          setRegistrationNumber(vehicleIdState.idValue);
          break;
        case VehicleIdType.Vin:
          setIdentificationNumber(vehicleIdState.idValue);
          break;
      }

      return;
    }
  }, []);

  useEffect(() => {
    if (CommonValidation.isNotEmpty(registrationNumber)) {
      if (searchCarOnPageLoad) {
        const controller = new AbortController();

        const fetchVehicle = () => {
          fetchVehicleByVrn(registrationNumber, controller.signal);
        };

        fetchVehicle();

        return () => {
          controller.abort();
        };
      } else {
        const newVehicle: VehicleType = {
          ...journey.vehicle.vehicleDetails,
          vrn: registrationNumber
        };

        updateLoadedVehicle(newVehicle);
      }
    }
  }, [registrationNumber]);

  useEffect(() => {
    if (CommonValidation.isNotEmpty(identificationNumber)) {
      if (
        !hideVinInput &&
        !appConfig.includeQuotes &&
        !landingPageContentSettings.useVinInput
      ) {
        const vehicleDetails: VehicleType = {
          ...vehicle.vehicleDetails,
          vin: identificationNumber
        };

        onUpdateVehicleDetails({
          ...vehicle,
          vehicleDetails: vehicleDetails
        });
      } else {
        const controller = new AbortController();

        const fetchVehicle = () => {
          fetchVehicleByVin(identificationNumber, controller.signal);
        };

        fetchVehicle();

        return () => {
          controller.abort();
        };
      }
    }
  }, [identificationNumber]);

  useEffect(() => {
    if (goForward === true && updateVehicleDetails === false) {
      navigate(PageRelativePath.Products);
      onPageChange(PageAbsolutePath.VehicleDetails, true);
    }
  }, [updateVehicleDetails]);

  const fetchVehicleByVrn = async (reg: string, signal: AbortSignal) => {
    setLoadingVehicle(true);

    await new VehicleApi().getRegisteredVehicles(
      reg,
      userToken,
      fetchVehicleSuccessVrn,
      fetchVehicleFailureVrn,
      signal
    );
  };

  const fetchVehicleByVin = async (id: string, signal: AbortSignal) => {
    setLoadingVehicle(true);

    await new VehicleApi().getRegisteredVehiclesVin(
      id,
      userToken,
      fetchVehicleSuccessVin,
      fetchVehicleFailureVin,
      signal
    );
  };

  const fetchVehicleSuccessVin = (userVehicle: VehicleType) => {
    if (
      userVehicle.registrationDate === '' ||
      userVehicle.registrationDate === null
    ) {
      userVehicle.errorOrigin = 'vin';
      userVehicle.errorMessage = t(
        'common-error-msg.api-error-msg.vehicle-vin-error'
      );
      userVehicle.vin = identificationNumber;
    }

    updateLoadedVehicle(userVehicle);
  };

  const fetchVehicleSuccessVrn = (userVehicle: VehicleType) => {
    if (
      userVehicle.registrationDate === '' ||
      userVehicle.registrationDate === null
    ) {
      userVehicle.errorOrigin = 'vrn';
      userVehicle.errorMessage = t(
        'common-error-msg.api-error-msg.vehicle-vreg-error'
      );
      userVehicle.vrn = registrationNumber;
    }

    updateLoadedVehicle(userVehicle);
  };

  const updateLoadedVehicle = (userVehicle: VehicleType) => {
    onUpdateVehicleDetails({
      ...journey.vehicle,
      vehicleDetails: userVehicle
    });

    setLoadingVehicle(false);
  };

  const fetchVehicleFailureVin = () => {
    const userVehicle = landingPageContentSettings.useVinInput
      ? vehicleDetails
      : initialVehicleDetails;

    userVehicle.errorOrigin = 'vin';
    userVehicle.errorMessage = t(
      'common-error-msg.api-error-msg.vehicle-vin-error'
    );
    userVehicle.vin = identificationNumber;

    updateLoadedVehicle(userVehicle);
  };

  const fetchVehicleFailureVrn = () => {
    const userVehicle = initialVehicleDetails;

    userVehicle.errorOrigin = 'vrn';
    userVehicle.errorMessage = t(
      'common-error-msg.api-error-msg.vehicle-vreg-error'
    );
    userVehicle.vrn = registrationNumber;

    updateLoadedVehicle(userVehicle);
  };

  const updateJourneyStateFromQuoteData = (quoteData: QuotesData) => {
    if (!quoteData) {
      if (onUpdateFoundExistingQuotes) {
        onUpdateFoundExistingQuotes(false);
      }
      return;
    }

    if (
      CommonValidation.isNotEmpty(
        quoteData.vehicleInfo.vehicleDetails.registrationDate
      )
    ) {
      if (CommonValidation.isNotEmpty(registrationNumber)) {
        quoteData.vehicleInfo.vehicleDetails.errorOrigin = 'vrn';
        quoteData.vehicleInfo.vehicleDetails.errorMessage = t(
          'common-error-msg.api-error-msg.quote-vreg-error'
        );
      } else if (CommonValidation.isNotEmpty(identificationNumber)) {
        quoteData.vehicleInfo.vehicleDetails.errorOrigin = 'vin';
        quoteData.vehicleInfo.vehicleDetails.errorMessage = t(
          'common-error-msg.api-error-msg.quote-vin-error'
        );
      } else {
        quoteData.vehicleInfo.vehicleDetails.errorOrigin = 'email';
        quoteData.vehicleInfo.vehicleDetails.errorMessage = t(
          'common-error-msg.api-error-msg.quote-email-error'
        );
      }
    }

    onUpdateVehicleDetails(quoteData.vehicleInfo);
    onUpdateProducts(quoteData.products.products);
    onUpdateManufacturingCoverageMonths(
      quoteData.products.manufacturingCoverageMonths || 0
    );
    onUpdateProductDocuments(quoteData.products.documents);
    onUpdateQuotedProducts(quoteData.quotedProducts);
    setPersonalDetails(quoteData.personalDetail);

    if (onUpdateQuotedByDealerReference)
      onUpdateQuotedByDealerReference(quoteData.vehicleInfo.dealerReference);
    if (onUpdateFoundExistingQuotes) {
      onUpdateFoundExistingQuotes(true);
    }

    setLoadingVehicle(false);

    if (setQuoteId) {
      setQuoteId(quoteData.id);
    }

    if (setHasQuoteExpired) {
      setHasQuoteExpired(quoteData.hasExpired);
    }

    navigate(PageRelativePath.Products);
    onPageChange(PageAbsolutePath.VehicleDetails, true);
  };

  const goToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const onBack = () => {
    const path = setCountryCode(
      PageAbsolutePath.Landing,
      appConfig.countryCode
    );
    navigate(path);
    onPageChange(PageAbsolutePath.VehicleDetails, false);
    goToTop();
  };

  const onSeeProductsClick = () => {
    setValidateVehicleDetails(true);
    setGoForward(true);
  };

  const vehicleNotFound =
    loadingVehicle ||
    vehicleDetails.registrationDate === '' ||
    vehicleDetails.registrationDate === null;

  const onUpdateVehicleState = (vs: VehicleState) => {
    onUpdateVehicleDetails(vs);
    setUpdateVehicleDetails(false);
  };

  const onVehicleValidated = (isValid: boolean) => {
    if (isValid && goForward) setUpdateVehicleDetails(true);
    setValidateVehicleDetails(false);
  };

  return (
    <>
      <Helmet>
        <title>{t('page-title.vehicle-details')}</title>
      </Helmet>

      <LoadingContainer
        loading={loadingVehicle}
        message={t('loading-placeholder.loading-text')}>
        <div>
          <div>
            <PageHeader
              title={cms.title}
              description={cms.description}
              information={cms.information}
              warning={cms.warning}
              descriptionClass='p2'
            />
            <VehiclePage
              initialVehicleInfo={vehicle}
              update={updateVehicleDetails || false}
              validate={validateVehicleDetails || false}
              onValidated={onVehicleValidated}
              onUpdateVehicleState={onUpdateVehicleState}
              onUpdateRegistrationNumber={(vrn) => setRegistrationNumber(vrn)}
              showEditVrn={searchCarOnPageLoad}
              showGapIntroductionDate={showGapDateField}
              showModelTrim={false}
              showPolicyStartDate={false}
              isPolicyStartDateEnabled={false}
              showSalesId={!appConfig.includeQuotes}
              showVehicleDeliveryDate={showVehicleDeliveryDate}
              showVehiclePurchasePrice={showVehiclePurchasePrice}
              showDealerReference={
                appConfig.includeQuotes || showExternalReference
              }
              showFieldsOnDataError={true}
              vehiclePurchasePriceLabel={t(
                'customer-vehicle-details-page.vehicle-purchase-price-label'
              )}
              token={userToken}
              showVin={
                appConfig.includeQuotes ||
                landingPageContentSettings.useVinInput ||
                !hideVinInput
              }
              showIsDeliveryDateEstimated={appConfig.includeQuotes}
              customisation={cms}
              onUpdateIdentificationNumber={(vin) =>
                setIdentificationNumber(vin)
              }
              isReadOnly={false}
              futureDateAllowed={appConfig.includeQuotes}
              showNeedHelpTooltipDeliveryDate={true}
              showEditVehicle={true}
              showMileage={true}
              isPurchasePriceEditable={true}
              isMileageEditable={true}
              isSalesIdMandatory={cms.mandatorySalesId}
              culture={appConfig.cultureCode}
              isVehicleDeliveryEditable={true}
              externalReferenceLabelText={externalReferenceLabelText}
              isDealerReferenceMandatory={
                showExternalReference && cms.mandatoryExternalReference
              }
              ignoreVinFromManualDetails={
                landingPageContentSettings.useVinInput || cms.mandatoryVin
              }
            />
            <div className='flex-row form-nav mobile-width-90pc'>
              <ButtonBack
                name='btnBack'
                onClick={onBack}
                text={t('customer-button.back-buttont-text')}
              />
              <ButtonContinue
                name='btn-see-products'
                text={t('customer-button.button-see-products')}
                onClick={onSeeProductsClick}
                disabled={vehicleNotFound}
              />
            </div>
          </div>
        </div>
      </LoadingContainer>
    </>
  );
};

export default VehicleDetailsPage;
